import {CompatClient, messageCallbackType, Stomp, StompHeaders, StompSubscription} from '@stomp/stompjs';
import SockJS from 'sockjs-client';

import {jwtToken} from '@/services/auth0';

class WebsocketService {
    private subscriptions: Record<string, {sub?: StompSubscription; callback?: messageCallbackType}> = {};
    private stompClient: CompatClient = null;
    private initializationCount = 0;

    public initWebSocket(successCallback?: () => void, errorCallback?: () => void) {
        if (this.stompClient?.connected) return;

        this.initializationCount = this.initializationCount + 1;
        this.stompClient = Stomp.over(
            () => new SockJS(`${window.REACT_APP_CORE_API_URL}/notifications/v1/notifications`),
        );

        this.stompClient.configure({
            debug: () => null,
            onStompError: () => {
                errorCallback && errorCallback();
            },
            onWebSocketError: () => {
                errorCallback && errorCallback();
            },
            onWebSocketClose: () => {
                console.info('socket closed');
                if (this.initializationCount < 2) {
                    setTimeout(() => {
                        console.info('socket reinitialization');
                        this.initWebSocket(successCallback, errorCallback);
                    }, 3000);
                } else {
                    errorCallback && errorCallback();
                }
            },
        });
        this.stompClient.connect(
            {Authorization: 'Bearer ' + jwtToken},
            () => {
                this.initializationCount = 0;

                // Re-subscribe to previous subscriptions
                Object.entries(this.subscriptions).forEach(([url, {callback}]) => {
                    if (callback) {
                        const sub = this.stompClient.subscribe(url, callback);
                        this.subscriptions[url] = {sub, callback};
                    }
                });

                successCallback?.();
            },
            () => {
                setTimeout(() => {
                    errorCallback && errorCallback();
                    this.initWebSocket(successCallback, errorCallback);
                }, 3000);
            },
        );
    }

    public subscribe(url: string, callback?: messageCallbackType) {
        if (!callback) return;

        if (this.stompClient?.connected) {
            const sub = this.stompClient.subscribe(url, callback);
            this.subscriptions[url] = {sub, callback};
        } else {
            this.subscriptions[url] = {callback};
        }
    }

    public unsubscribe(url: string, callback?: (headers?: StompHeaders) => void) {
        if (this.stompClient?.connected) {
            this.subscriptions?.[url]?.sub?.unsubscribe();
            delete this.subscriptions?.[url];
            callback && callback();
        }
    }

    public sendMessage(url: string, data: string) {
        if (this.stompClient?.connected) this.stompClient.send(url, {}, data);
    }

    public disconnect(callback?: () => void) {
        if (this.stompClient?.connected) {
            this.stompClient.disconnect(() => {
                callback && callback();
            });
        }
    }
}

const websocketService = new WebsocketService();

export default websocketService;
